import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, FlaskConical, Loader2 } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import type { CategoriaTarifaria, SimulacionDeTarifa } from '@/shared/api/tipos';
import { RURALIDAD_ETIQUETA, tituloDeTarifa } from './nombres';

const RURALIDADES = [
  { valor: '', etiqueta: 'Sin indicar' },
  { valor: 'urbano', etiqueta: 'Urbano' },
  { valor: 'rural', etiqueta: 'Rural' },
];

const CONSUMOS = [
  { valor: '', etiqueta: 'Sin indicar' },
  { valor: 'residencial', etiqueta: 'Residencial' },
  { valor: 'comercial', etiqueta: 'Comercial' },
  { valor: 'industrial', etiqueta: 'Industrial' },
];

export function SimuladorDeTarifa({ categorias }: { categorias: CategoriaTarifaria[] }) {
  const [destino, setDestino] = useState('');
  const [ruralidad, setRuralidad] = useState('');
  const [consumo, setConsumo] = useState('');

  const hayPregunta = destino !== '' || consumo !== '';

  const simulacion = useQuery({
    queryKey: ['tarifas', 'simulacion', destino, ruralidad, consumo],
    queryFn: () =>
      api.get<SimulacionDeTarifa>('/tarifas/simulacion', {
        query: {
          destinoTarifario: destino || undefined,
          ruralidad: ruralidad || undefined,
          tipoConsumo: consumo || undefined,
        },
      }),
    enabled: hayPregunta,
  });

  return (
    <section className="overflow-hidden rounded-xl border border-armazon-200 bg-white shadow-sm">
      <header className="flex items-center gap-2 border-b border-armazon-100 bg-armazon-50/60 px-6 py-4">
        <span className="grid size-6 shrink-0 place-items-center rounded-md bg-amber-100 text-amber-700">
          <FlaskConical className="size-3" aria-hidden />
        </span>
        <h2 className="text-sm font-bold uppercase tracking-wide text-armazon-800">Simulador</h2>
        <span className="ml-auto text-[11px] uppercase text-armazon-500">
          Qué se cobraría, antes de emitir
        </span>
      </header>

      <div className="grid gap-4 p-6 lg:grid-cols-[1fr_auto]">
        <div className="grid gap-4 sm:grid-cols-3">
          <Campo etiqueta="Destino tarifario">
            <select
              value={destino}
              onChange={(e) => setDestino(e.target.value)}
              className={entrada}
            >
              <option value="">Sin indicar</option>
              {categorias.map((c) => (
                <option key={c.id} value={c.nombre}>
                  {c.nombre}
                </option>
              ))}
            </select>
          </Campo>

          <Campo etiqueta="Ruralidad">
            <select
              value={ruralidad}
              onChange={(e) => setRuralidad(e.target.value)}
              className={entrada}
            >
              {RURALIDADES.map((r) => (
                <option key={r.valor} value={r.valor}>
                  {r.etiqueta}
                </option>
              ))}
            </select>
          </Campo>

          <Campo etiqueta="Tipo de consumo" ayuda="Solo si no hay destino tarifario">
            <select
              value={consumo}
              onChange={(e) => setConsumo(e.target.value)}
              className={entrada}
            >
              {CONSUMOS.map((c) => (
                <option key={c.valor} value={c.valor}>
                  {c.etiqueta}
                </option>
              ))}
            </select>
          </Campo>
        </div>

        <div className="lg:w-72">{resultado()}</div>
      </div>
    </section>
  );

  function resultado() {
    if (!hayPregunta) {
      return (
        <div className="grid h-full min-h-24 place-items-center rounded-xl border border-dashed border-armazon-200 px-4 py-3 text-center">
          <p className="text-[11px] uppercase text-armazon-500">
            Elija un destino o un tipo de consumo
          </p>
        </div>
      );
    }

    if (simulacion.isPending) {
      return (
        <div className="grid h-full min-h-24 place-items-center rounded-xl border border-armazon-200 px-4 py-3">
          <Loader2 className="size-4 animate-spin text-armazon-400" aria-hidden />
        </div>
      );
    }

    const s = simulacion.data;
    if (!s) {
      return null;
    }

    if (s.sinTarifa) {
      return (
        <div className="grid h-full min-h-24 content-center gap-1 rounded-xl border-2 border-amber-300 bg-amber-50 px-4 py-3">
          <p className="flex items-center gap-1.5 text-xs font-bold uppercase text-amber-900">
            <AlertTriangle className="size-3.5 shrink-0" aria-hidden />
            No aplica ninguna tarifa
          </p>
          <p className="importe text-2xl font-bold text-amber-900">$0.00</p>
          <p className="text-[11px] uppercase text-amber-800">A esa cuenta se le cobraría cero</p>
        </div>
      );
    }

    return (
      <div className="grid h-full min-h-24 content-center gap-1 rounded-xl border-2 border-marca-300 bg-marca-50 px-4 py-3">
        <p className="flex items-start gap-1.5 text-xs font-bold uppercase leading-tight text-marca-900">
          <CheckCircle2 className="mt-px size-3.5 shrink-0" aria-hidden />
          {s.tarifa ? tituloDeTarifa(s.tarifa) : ''}
        </p>
        <p className="importe text-2xl font-bold text-marca-900">${s.subtotal.toFixed(2)}</p>
        <p className="text-[11px] uppercase tracking-wide text-marca-700">
          {[s.tarifa?.categoria, RURALIDAD_ETIQUETA[s.tarifa?.ruralidad ?? '']]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>
    );
  }
}

function Campo({
  etiqueta,
  ayuda,
  children,
}: {
  etiqueta: string;
  ayuda?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="grid content-start gap-1.5">
      <span className="text-xs font-semibold uppercase tracking-wider text-armazon-600">
        {etiqueta}
      </span>
      {children}
      {ayuda && (
        <span className="text-[11px] uppercase tracking-[0.03em] text-armazon-600">{ayuda}</span>
      )}
    </label>
  );
}

const entrada =
  'h-11 w-full rounded-lg border border-armazon-200 bg-white px-3 text-sm text-armazon-900 outline-none transition-colors focus:border-marca-400 focus:ring-2 focus:ring-marca-500/20';
